import { memo, useEffect, useMemo, useRef } from "react";

import usePolledLazyQuery from "apollo/usePolledQuery/usePolledLazyQuery";
import Filters from "components/Filters";
import FiltersContentWrapper from "components/Filters/ContentWrapper";
import FiltersPanel from "components/Filters/FiltersPanel";
import { useCachedFilterFields } from "components/Filters/hooks";
import FiltersSidebar from "components/Filters/Sidebar";
import SortDropdown from "components/Filters/SortDropdown";
import FiltersSplit from "components/Filters/Split";
import { SavedFilterView, SortOption } from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import ViewCustomizationDropdown from "components/ViewCustomization/Dropdown";
import { searchModuleSuggestionsDictionary } from "constants/module";
import Box from "ds/components/Box";
import Toggle from "ds/components/Toggle";
import { AnalyticsPageModule } from "hooks/useAnalytics/pages/module";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { SearchQueryPredicate, SearchSuggestionsOutput } from "types/generated";
import { PollingIntervalGroups } from "apollo/constants";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import {
  EXCLUDED_FILTER_FIELDS,
  FILTERS_ORDER_SETTINGS_KEY,
  initialSortDirection,
  initialSortOption,
} from "../../constants";
import { SEARCH_MODULES_SUGGESTIONS } from "../../gql";
import { getFilterKey } from "../../helpers";

type ModuleFiltersLayoutProps = {
  allSelected: boolean;
  onSelectAll: () => void;
  onResetAll: () => void;
  predicates: SearchQueryPredicate[];
  children: React.ReactNode;
  hasItems: boolean;
  currentSavedView?: SavedFilterView;
  setCurrentSavedView: (view?: SavedFilterView) => unknown;
};

const ModuleFiltersLayout = ({
  allSelected,
  onSelectAll,
  onResetAll,
  predicates,
  children,
  hasItems,
  currentSavedView,
  setCurrentSavedView,
}: ModuleFiltersLayoutProps) => {
  const fieldsRef = useRef<string[] | null>(null);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const { onError } = useTypedContext(FlashContext);

  const [loadSearchModulesSuggestions, { data: filteringData, loading, refetch }] =
    usePolledLazyQuery<{
      searchModulesSuggestions: SearchSuggestionsOutput;
    }>(SEARCH_MODULES_SUGGESTIONS, { pollingGroup: PollingIntervalGroups.Lists });

  const cachedFiltersData = useCachedFilterFields(filteringData?.searchModulesSuggestions?.fields);

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter((field) => field.orderable)
        .map((field) => {
          return {
            value: field.name,
            // TODO: distinguish labels from keys
            label: getFilterKey(field.name),
          };
        }) || []
    );
  }, [cachedFiltersData]);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: searchModuleSuggestionsDictionary,
    excludedFilterFields: EXCLUDED_FILTER_FIELDS,
  });

  const handleSelectAllToggle = () => {
    if (!allSelected) {
      onSelectAll();
    } else {
      onResetAll();
    }
  };

  const loadSearchSuggestions = (initial = false) => {
    try {
      loadSearchModulesSuggestions({
        variables: {
          input: {
            fullTextSearch: searchInput,
            predicates,
            fields: initial ? null : fieldsRef.current,
          },
        },
      });
    } catch (error) {
      onError(error);
    }
  };

  const handlePollingActiveSections = (fields: string[]) => {
    fieldsRef.current = fields;

    refetch({
      input: {
        fullTextSearch: searchInput,
        predicates,
        fields,
      },
    });
  };

  useEffect(() => {
    loadSearchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predicates, searchInput]);

  // initial request with nullish fields
  useEffect(() => {
    loadSearchSuggestions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Filters
      filters={filters}
      filtersItemsOptionsMap={filterOptionsMap}
      filtersLoading={loading}
      sortOptions={sortOptions}
      initialSortOption={initialSortOption}
      initialSortDirection={initialSortDirection}
      filtersDictionary={searchModuleSuggestionsDictionary}
      pollActiveSections={handlePollingActiveSections}
      filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
      filtersType="modules"
      currentSavedView={currentSavedView}
      setCurrentSavedView={setCurrentSavedView}
    >
      <FiltersSplit>
        <FiltersSidebar analyticsPage={AnalyticsPageModule.ModulesList} />

        <FiltersContentWrapper>
          {hasItems && (
            <FiltersPanel>
              <Box direction="row">
                <Toggle
                  variant="checkbox"
                  isSelected={allSelected}
                  onChange={handleSelectAllToggle}
                  aria-label={allSelected ? "Unselect all" : "Select all"}
                >
                  {allSelected ? "Unselect all" : "Select all"}
                </Toggle>
              </Box>

              <Box align="center" gap="medium">
                <SortDropdown />
                <ViewCustomizationDropdown analyticsPage={AnalyticsPageModule.ModulesList} />
              </Box>
            </FiltersPanel>
          )}
          {children}
        </FiltersContentWrapper>
      </FiltersSplit>
    </Filters>
  );
};

export default memo(ModuleFiltersLayout);
